import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import scraperApi from 'api';
import { useDebouncer } from 'hooks/useDebouncer';
import { formatStatusDisplay } from 'pages/crawler/utils/crawlerStatusUtils';
import { getOutputFormat } from 'pages/crawler/utils/crawlerFormUtils';
import { RequestStatusFilter, type JobUrl, type PaginationInfo } from 'pages/crawler/components/jobs/types';
import { JOB_URLS_PAGE_SIZE, JOB_URLS_POLL_INTERVAL, SEARCH_DEBOUNCE_MS, TERMINAL_STATES } from 'pages/crawler/constants/crawlerConstants';

export type { PaginationInfo } from 'pages/crawler/components/jobs/types';

interface UseJobUrlsPaginationReturn {
  // Table data
  urls: JobUrl[];
  pagination: PaginationInfo | null;

  // Job-level data (from same response)
  jobData: any | null;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Controls
  searchInput: string;
  setSearchInput: (value: string) => void;
  setPage: (page: number) => void;
  statusFilter: RequestStatusFilter;
  setStatusFilter: (value: RequestStatusFilter) => void;
  refetch: () => void;
}

const parsePagination = (paginationData: any, fallbackPage: number, fallbackTotal: number): PaginationInfo => {
  if (paginationData) {
    return {
      page: paginationData.page ?? fallbackPage,
      limit: paginationData.limit ?? JOB_URLS_PAGE_SIZE,
      total: paginationData.total ?? fallbackTotal,
      hasNextPage: paginationData.hasNextPage ?? false,
    };
  }
  return { page: fallbackPage, limit: JOB_URLS_PAGE_SIZE, total: fallbackTotal, hasNextPage: false };
};

const transformUrlRow = (req: any): JobUrl => ({
  id: req.id || req.queueRequestId,
  url: req.url,
  depth: req.depth ?? req.currentDepth,
  status: formatStatusDisplay(req.status || req.requestStatus),
  cost: req.cost ?? req.requestCost,
  crawledAt: req.updatedAt,
  createdAt: req.createdAt,
  updatedAt: req.updatedAt,
  queueRequestId: req.queueRequestId,
  resultKey: req.resultKey ?? req.doResultKey,
});

const transformJobData = (rawJobData: any) => {
  const apiParams = rawJobData.apiParams || {};
  const deviceRaw = apiParams.device_type || apiParams.deviceType || 'desktop';
  const deviceType = String(deviceRaw).charAt(0).toUpperCase() + String(deviceRaw).slice(1);
  const retry404 = Boolean(apiParams.retry_404 ?? apiParams.retry404);
  const outputFormat = getOutputFormat(apiParams);
  const autoparse = apiParams.autoparse;
  const stats = rawJobData.stats || {};

  return {
    ...rawJobData,
    status: formatStatusDisplay(rawJobData.status),
    startUrl: rawJobData.startUrl || '',
    apiParams,
    outputFormat,
    autoparse,
    deviceType,
    retry404,
    stats: {
      urlsCrawled: stats.successfullyCrawledRequests || 0,
      inProgress: stats.runningRequests || 0,
      cancelled: stats.cancelledRequests || 0,
      failed: stats.failedRequests || 0,
      totalRequests: stats.totalRequests || 0,
    },
  };
};

export const useJobUrlsPagination = (jobId?: string): UseJobUrlsPaginationReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read pagination state from URL search params
  const currentPage = Math.max(1, Math.floor(Number(searchParams.get('page')) || 1));
  const currentSearch = searchParams.get('search') ?? '';
  const currentStatusFilter = (searchParams.get('status') as RequestStatusFilter) || 'all';

  // Local search input state for responsive typing
  const [searchInput, setSearchInput] = useState(currentSearch);
  const debouncedSearch = useDebouncer(searchInput, SEARCH_DEBOUNCE_MS);

  // Data state
  const [urls, setUrls] = useState<JobUrl[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [jobData, setJobData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchIdRef = useRef(0);
  const isJobTerminal = useRef(false);
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync debounced search to URL params (resets page to 1)
  useEffect(() => {
    if (debouncedSearch === currentSearch) return;

    const newParams = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      newParams.set('search', debouncedSearch);
    } else {
      newParams.delete('search');
    }
    newParams.delete('page');
    setSearchParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Sync local searchInput with URL on back/forward navigation
  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  const setPage = useCallback((page: number) => {
    if (page === currentPage) return;
    setIsLoading(true);
    const newParams = new URLSearchParams(searchParams);
    if (page > 1) {
      newParams.set('page', String(page));
    } else {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, currentPage]);

  const setStatusFilter = useCallback((value: RequestStatusFilter) => {
    if (value === currentStatusFilter) return;
    setIsLoading(true);
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('status', value);
    } else {
      newParams.delete('status');
    }
    newParams.delete('page');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, currentStatusFilter]);

  // Main fetch. `isBackgroundPoll` skips the loading spinner (used for polling).
  const fetchJobSummary = useCallback(async (isBackgroundPoll = false) => {
    if (!jobId) {
      setUrls([]);
      setPagination(null);
      setJobData(null);
      setIsLoading(false);
      return;
    }

    // Skip polling for finished jobs
    if (isBackgroundPoll && isJobTerminal.current) {
      return;
    }

    // Cancel previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const currentFetchId = ++fetchIdRef.current;

    if (!isBackgroundPoll) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const params = {
        page: currentPage,
        limit: JOB_URLS_PAGE_SIZE,
        ...(currentSearch && { search: currentSearch }),
        ...(currentStatusFilter !== 'all' && { status: currentStatusFilter }),
      };

      const response = await scraperApi.crawlers.getCrawlerJobSummary(
        jobId,
        params,
        { signal: abortController.signal }
      );

      // Ignore if a newer fetch was triggered
      if (currentFetchId !== fetchIdRef.current) return;

      if (response.success && response.data) {
        const data = response.data;

        // Extract and transform job-level data
        const job = transformJobData(data);
        setJobData(job);

        // Detect terminal state
        isJobTerminal.current = TERMINAL_STATES.includes(job.status.state);

        // Extract paginated URL rows
        const rows: JobUrl[] = (data.requests || []).map(transformUrlRow);

        const paginationInfo = parsePagination(data.pagination, currentPage, rows.length);
        setPagination(paginationInfo);

        // Reconcile URL if backend clamped the page (e.g. user requested page 999 but only 5 pages exist)
        if (!isBackgroundPoll && paginationInfo.page !== currentPage && paginationInfo.total > 0) {
          const newParams = new URLSearchParams(searchParams);
          if (paginationInfo.page > 1) {
            newParams.set('page', String(paginationInfo.page));
          } else {
            newParams.delete('page');
          }
          setSearchParams(newParams, { replace: true });
        }

        setUrls(rows);
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return;
      }

      if (currentFetchId !== fetchIdRef.current) return;
      console.error('Error fetching job summary:', err);
      setError(err.message || 'Failed to fetch job data');
      setUrls([]);
      setPagination(null);
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, currentPage, currentSearch, currentStatusFilter]);

  // Start or restart the polling timer
  const startPollingTimer = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
    }
    pollingTimerRef.current = setInterval(() => {
      fetchJobSummary(true);
    }, JOB_URLS_POLL_INTERVAL);
  }, [fetchJobSummary]);

  // Fetch on mount and when URL params change — resets polling timer
  useEffect(() => {
    fetchJobSummary();
    startPollingTimer();

    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }
    };
  }, [fetchJobSummary, startPollingTimer]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const refetch = useCallback(() => {
    isJobTerminal.current = false;
    fetchJobSummary();
    startPollingTimer();
  }, [fetchJobSummary, startPollingTimer]);

  return {
    urls,
    pagination,
    jobData,
    isLoading,
    error,
    searchInput,
    setSearchInput,
    setPage,
    statusFilter: currentStatusFilter,
    setStatusFilter,
    refetch,
  };
};
